import './DataForm.css';
import { Row, Col, InputNumber, Radio, Checkbox } from 'antd';
import React, { ElementType } from 'react';
import { Formik, Form, Field, FormikHelpers, FormikValues, getIn } from 'formik';
import { Button, Input, Form as AntForm, DatePicker } from 'antd';
import * as Yup from 'yup';
import { Dayjs } from 'dayjs';
import DataFormCustomSelect from './DataFormCustomSelect';
import { useTranslation } from 'react-i18next';
import { evaluate } from 'mathjs'
const { Item: FormItem } = AntForm;

type FieldType = 'title' | 'text' | 'number' | 'select' | 'password' | 'email' | 'textarea' | 'date' | 'radio' | 'calculated' | 'checkbox';

export interface DataFieldPropsBase {
  label?: string;
  name: string;
  type: FieldType;
  placeholder: string;
  component?: ElementType<any>;
  columnIndex?: number;
  rowIndex?: number;
  content?: string;
  precision?: number;
  step?: number;
  options?: any[];
  onChange?: (value: any, setFieldValue: (field: string, value: any) => void) => void;
  disabledDate?: (current: Dayjs) => boolean;
}

export interface SelectFieldProps extends DataFieldPropsBase {
  type: 'select';
  options: {
    key?: string;
    value: any;
    label: string;
  }[];
}

export interface TitleFieldProps extends DataFieldPropsBase {
  type: 'title';
  content: string;
}

export type DataFieldProps = DataFieldPropsBase | SelectFieldProps | TitleFieldProps;

interface FormProps {
  initialValues: FormikValues;
  validationSchema?: Yup.ObjectSchema<any>;
  onSubmit: (
    values: FormikValues,
    formikHelpers?: FormikHelpers<FormikValues>,
  ) => void | Promise<void>;
  fields: DataFieldProps[];
  onCancel: () => void;
  numberOfColumns?: number;
}

const DataForm: React.FC<FormProps> = ({
  initialValues,
  validationSchema,
  onSubmit,
  fields,
  onCancel,
  numberOfColumns = 1
}) => {
  const { t } = useTranslation();

  // Responsive columns: 1 on mobile, 2 on tablet, 3 on desktop
  const [columns, setColumns] = React.useState(numberOfColumns);

  React.useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 600) setColumns(1);
      else if (window.innerWidth < 1200) setColumns(2);
      else setColumns(3);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [numberOfColumns]);

  const groupedFields = fields.reduce((acc, field, index) => {
    const row = field.rowIndex !== undefined ? field.rowIndex : Math.floor(index / columns);

    if (!acc[row]) {
      acc[row] = [];
    }

    acc[row].push(field);
    return acc;
  }, {} as { [key: number]: DataFieldProps[] });

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={(values, formikHelpers) => {
        onSubmit(values, formikHelpers);
      }}
      enableReinitialize
    >
      {({ isSubmitting, errors, touched, setFieldValue, resetForm, isValid, dirty, values }) => {
        // Check if the form only contains a radio field and it is preselected
        const onlyRadio =
          fields.length === 1 &&
          fields[0].type === 'radio' &&
          values[fields[0].name] !== undefined &&
          values[fields[0].name] !== null &&
          values[fields[0].name] !== '';

        const canSubmit = (isValid) || onlyRadio;

        return (
          <Form>
            {Object.entries(groupedFields).map(([rowIndex, rowFields]) => (
              <Row key={rowIndex} gutter={[16, 8]} className="dataform-row">
                {rowFields.map((field, index) => {
                  if (field.type === 'title') {
                    return (
                      <Col key={`title-${index}`} span={24}>
                        <div className="dataform-title">
                          {field.content}
                        </div>
                      </Col>
                    );
                  }

                  let colSpan = 24;
                  if (columns === 2) colSpan = 12;
                  if (columns === 3) colSpan = 8;

                  return (
                    <Col key={`field-${index}`} span={colSpan} className="dataform-col">
                      <FormItem
                        layout='vertical'
                        label={field.label ? <span>{field.label}</span> : null}
                        validateStatus={
                          getIn(touched, field.name) && getIn(errors, field.name)
                            ? 'error'
                            : ''
                        } help={
                          getIn(touched, field.name) && getIn(errors, field.name)
                            ? String(getIn(errors, field.name))
                            : ''
                        }
                      >
                        {renderField(field, setFieldValue, touched, errors)}
                      </FormItem>
                    </Col>
                  );
                })}
              </Row>
            ))}

            <div className="dataform-actions">
              <Button
                type="default"
                onClick={() => {
                  onCancel();
                  resetForm();
                }}
                style={{ marginRight: '0.5em' }}
              >
                {t('general.cancel')}
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={isSubmitting}
                disabled={!canSubmit}
                style={{
                  background: !canSubmit ? '#d9d9d9' : undefined,
                  borderColor: !canSubmit ? '#d9d9d9' : undefined,
                  color: !canSubmit ? '#888' : undefined,
                  cursor: !canSubmit ? 'not-allowed' : undefined,
                }}
              >
                {t('general.submit')}
              </Button>
            </div>
          </Form>
        );
      }}
    </Formik>
  );
};

const renderField = (field: DataFieldProps, setFieldValue: any, touched: any, errors: any) => {
  const placeholder = field.placeholder;

  switch (field.type) {
    case 'radio':
      return (
        <Field name={field.name}>
          {({ field: formikField }: { field: any }) => (
            <Radio.Group
              value={formikField.value}
              onChange={(e) => setFieldValue(field.name, e.target.value)}
            >
              {(field.options || []).map((option: any, idx: number) => (
                <Radio key={idx} value={option.value}>
                  {option.label}
                </Radio>
              ))}
            </Radio.Group>
          )}
        </Field>
      );
    case 'select':
      return (
        <Field
          name={field.name}
          placeholder={placeholder}
          options={(field as SelectFieldProps).options || []}
          component={DataFormCustomSelect}
          style={{ display: 'block' }}
          status={touched[field.name || ''] && errors[field.name || ''] ? 'error' : ''}
        />
      );
    case 'date':
      return (
        <Field name={field.name}>
          {({ field: formikField }: { field: any }) => (
            <DatePicker
              value={formikField.value ? formikField.value : null}
              onChange={(date: Dayjs | null) => setFieldValue(formikField.name || '', date)}
              placeholder={formikField.placeholder}
              style={{ width: '100%' }}
              format={'DD.MM.YYYY'}
              status={touched[formikField.name || ''] && errors[formikField.name || ''] ? 'error' : ''}
              disabledDate={field.disabledDate}
            />
          )}
        </Field>
      );
    case 'textarea':
      return (
        <Field name={field.name}>
          {({ field }: { field: any }) => (
            <Input.TextArea
              {...field}
              placeholder={placeholder}
              status={touched[field.name || ''] && errors[field.name || ''] ? 'error' : ''}
            />
          )}
        </Field>
      );
    case 'number':
      return (
        <Field name={field.name}>
          {({ field, form }: { field: any; form: any }) => (
            <InputNumber
              {...field}
              value={form.values[field.name]}
              onChange={(value: number | null) => {
                form.setFieldValue(field.name, value !== null ? value : 0);
              }}
              decimalSeparator=','
              placeholder={field.placeholder}
              style={{ width: '100%' }}
              status={touched[field.name || ''] && errors[field.name || ''] ? 'error' : ''}
            />
          )}
        </Field>
      );
    case 'calculated':
      return (
        <Field name={field.name}>
          {({ field, form }: { field: any; form: any }) => (
            <Input
              {...field}
              value={form.values[field.name]}
              onBlur={() => {
                try {
                  const result = evaluate(form.values[field.name]);
                  form.setFieldValue(field.name, result);
                } catch (e) {
                  form.setFieldValue(field.name, 'Invalid expression');
                }
              }}
              placeholder={field.placeholder || 'Enter calculation'}
              status={touched[field.name || ''] && errors[field.name || ''] ? 'error' : ''}
            />
          )}
        </Field>
      );
    case 'checkbox':
      return (
        <Field name={field.name}>
          {({ field: formikField }: { field: any }) => (
            <Checkbox
              checked={formikField.value}
              onChange={(e) => setFieldValue(field.name, e.target.checked)}
            >
            </Checkbox>
          )}
        </Field>
      );
    default:
      return (
        <Field name={field.name}>
          {({ field }: { field: any }) => (
            <Input
              {...field}
              value={field.value}
              type={field.type}
              placeholder={placeholder}

              status={touched[field.name || ''] && errors[field.name || ''] ? 'error' : ''}
            />
          )}
        </Field>
      );
  }
};

export default DataForm;